/* PR-G16 — Reebelo / Apple-style variant picker behaviour.
 *
 * Vanilla JS, no jQuery, no framework. Each picker root has:
 *   • <script type="application/json" data-fdvp-variants>  — full variant catalogue
 *   • <button data-fdvp-option="N" data-fdvp-value="V">    — option control
 *   • <input  data-fdvp-id-input>                           — hidden cart-add input
 *   • <span   data-fdvp-price>, data-fdvp-compare           — live price card
 *   • <span   data-fdvp-stock>                              — stock pill
 *   • <button data-fdvp-add-button>                         — add-to-cart
 *
 * On every option click we:
 *   1. Update the selected option for that position.
 *   2. Search the variant catalogue for the matching variant.
 *   3. If found → update price, stock, ID, button. Push state via
 *      ?variant=ID. Mark unavailable adjacent options for visual
 *      feedback. Fire a generic CustomEvent('variant:change') so
 *      other theme widgets (image gallery, related products) can
 *      react if they want to.
 *   4. If no match → mark add-to-cart disabled, show "Unavailable".
 */

(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  // Extremely small money formatter. Shopify themes ship Shopify.formatMoney
  // when the slate `option_selection.js` is loaded; we use that when
  // available and fall back to a plain `${cents/100}.00` otherwise.
  function formatMoney(cents, format) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try { return window.Shopify.formatMoney(cents, format); }
      catch (_) { /* fall through */ }
    }
    var n = (cents / 100);
    return '$' + n.toFixed(2);
  }

  function init(root) {
    var variantsTag = root.querySelector('[data-fdvp-variants]');
    var idInput     = root.querySelector('[data-fdvp-id-input]');
    var priceEl     = root.querySelector('[data-fdvp-price]');
    var compareEl   = root.querySelector('[data-fdvp-compare]');
    var stockEl     = root.querySelector('[data-fdvp-stock]');
    var addBtn      = root.querySelector('[data-fdvp-add-button]');
    var addLabel    = root.querySelector('[data-fdvp-add-label]');
    var currentColorEl = root.querySelector('[data-fdvp-current-color]');
    if (!variantsTag || !idInput || !priceEl || !addBtn) return;

    var variants = [];
    try { variants = JSON.parse(variantsTag.textContent || '[]'); }
    catch (e) { variants = []; }
    if (!Array.isArray(variants) || !variants.length) return;

    var moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';

    // Resolve the current selection by reading every group's
    // `.is-selected` button. This is more robust than maintaining a
    // separate state object — the DOM is always the single source.
    function readSelection() {
      var selection = { 1: null, 2: null, 3: null, 4: null };
      var rows = root.querySelectorAll('[data-fdvp-option-row]');
      rows.forEach(function (row) {
        var pos = parseInt(row.getAttribute('data-option-position'), 10);
        if (!pos) return;
        var sel = row.querySelector('[data-fdvp-option][data-fdvp-value].is-selected');
        if (sel) selection[pos] = sel.getAttribute('data-fdvp-value');
      });
      return selection;
    }

    // Find the variant whose option1..option4 strictly equals each set
    // value. A null in `sel` is treated as a wildcard. option4 carries
    // the bundle axis added in PR-G17b — products without a bundle
    // option will have v.option4 = null and will only match when sel[4]
    // is also null (wildcard), which is what we want.
    function findVariant(sel) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (sel[1] != null && v.option1 !== sel[1]) continue;
        if (sel[2] != null && v.option2 !== sel[2]) continue;
        if (sel[3] != null && v.option3 !== sel[3]) continue;
        if (sel[4] != null && v.option4 !== sel[4]) continue;
        return v;
      }
      return null;
    }

    // For each option in `pos`, see whether any variant exists that
    // matches the rest of the current selection AND uses that value.
    // Mark unavailable values with .is-unavailable so the user sees
    // they can't combine them.
    function refreshAvailability(sel) {
      var rows = root.querySelectorAll('[data-fdvp-option-row]');
      rows.forEach(function (row) {
        var pos = parseInt(row.getAttribute('data-option-position'), 10);
        if (!pos) return;
        var btns = row.querySelectorAll('[data-fdvp-option][data-fdvp-value]');
        btns.forEach(function (btn) {
          var v = btn.getAttribute('data-fdvp-value');
          // Try a hypothetical selection: this position = v, others = current.
          var test = Object.assign({}, sel);
          test[pos] = v;
          var match = findVariant(test);
          var unavailable = !match || !match.available;
          btn.classList.toggle('is-unavailable', !!unavailable);
        });
      });
    }

    function update(opts) {
      opts = opts || {};
      var sel = readSelection();
      var variant = findVariant(sel);

      refreshAvailability(sel);

      // Update the colour-name caption next to the swatches.
      if (currentColorEl) {
        // Look for the row whose label-name contains "color".
        var colorRow = Array.prototype.slice.call(root.querySelectorAll('[data-fdvp-option-row]'))
          .find(function (r) {
            var nm = (r.getAttribute('data-option-name') || '').toLowerCase();
            return nm.indexOf('color') >= 0 || nm.indexOf('colour') >= 0;
          });
        if (colorRow) {
          var pos = parseInt(colorRow.getAttribute('data-option-position'), 10);
          if (sel[pos]) currentColorEl.textContent = sel[pos];
        }
      }

      if (!variant) {
        // Unavailable combination — keep the price card as-is, but
        // freeze add-to-cart.
        addBtn.classList.add('is-disabled');
        addBtn.setAttribute('disabled', 'disabled');
        if (addLabel) addLabel.textContent = 'Unavailable';
        if (stockEl) {
          stockEl.textContent = 'Unavailable';
          stockEl.classList.remove('fdvp-stock-in');
          stockEl.classList.add('fdvp-stock-out');
        }
        return;
      }

      // Update form input + URL.
      idInput.value = variant.id;
      root.setAttribute('data-current-variant-id', variant.id);
      if (opts.pushUrl !== false && window.history && window.history.replaceState) {
        try {
          var u = new URL(window.location.href);
          u.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', u.toString());
        } catch (_) { /* ignore */ }
      }

      // Update price card.
      priceEl.textContent = formatMoney(variant.price, moneyFormat);
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = formatMoney(variant.compare_at_price, moneyFormat);
          compareEl.removeAttribute('hidden');
        } else {
          compareEl.textContent = '';
          compareEl.setAttribute('hidden', 'hidden');
        }
      }

      // Update stock pill.
      if (stockEl) {
        if (variant.available) {
          if (variant.inventory_quantity > 0 && variant.inventory_quantity < 11) {
            stockEl.textContent = 'Only ' + variant.inventory_quantity + ' left';
          } else {
            stockEl.textContent = 'In stock';
          }
          stockEl.classList.add('fdvp-stock-in');
          stockEl.classList.remove('fdvp-stock-out');
        } else {
          stockEl.textContent = 'Sold out';
          stockEl.classList.remove('fdvp-stock-in');
          stockEl.classList.add('fdvp-stock-out');
        }
      }

      // Update add-to-cart.
      if (variant.available) {
        addBtn.classList.remove('is-disabled');
        addBtn.removeAttribute('disabled');
        if (addLabel) addLabel.textContent = 'Add to cart';
      } else {
        addBtn.classList.add('is-disabled');
        addBtn.setAttribute('disabled', 'disabled');
        if (addLabel) addLabel.textContent = 'Sold out';
      }

      // Notify the rest of the theme.
      try {
        root.dispatchEvent(new CustomEvent('variant:change', {
          bubbles: true,
          detail: { variant: variant }
        }));
      } catch (_) { /* old browsers */ }
    }

    // Click delegation: any option button toggles selected state in
    // its own group.
    root.addEventListener('click', function (ev) {
      var btn = ev.target.closest('[data-fdvp-option][data-fdvp-value]');
      if (!btn) return;
      if (btn.classList.contains('is-unavailable')) {
        // Still allow selection — the user might have arrived at a
        // combo we marked unavailable as a side effect; let the
        // update() pass surface "Unavailable" rather than swallow
        // the click.
      }
      var row = btn.closest('[data-fdvp-option-row]');
      if (!row) return;
      ev.preventDefault();
      row.querySelectorAll('[data-fdvp-option][data-fdvp-value].is-selected')
        .forEach(function (other) { other.classList.remove('is-selected'); });
      btn.classList.add('is-selected');
      update();
    });

    // Initial refresh — ensures unavailability marks render right
    // away on page load when the URL had ?variant=N already applied.
    update({ pushUrl: false });
  }

  ready(function () {
    document.querySelectorAll('[data-fdvp-root]').forEach(init);
  });
})();
